import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BarChart3,
  Bookmark,
  ChevronRight,
  Database,
  Grid3x3,
  Images,
  Inbox,
  ListPlus,
  Package,
  Percent,
  RefreshCw,
  Scale,
  ScanLine,
  Search,
  ShoppingCart,
  Sparkles,
  Tag,
  Upload,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { roleAtLeast, useRole, type StoreRole } from "@/lib/auth";

export const Route = createFileRoute("/tools/")({
  component: ToolsIndexPage,
});

interface ToolEntry {
  to: string;
  title: string;
  description: string;
  Icon: LucideIcon;
  /** Minimum role to see/open this tool. Omitted = available to all (Staff+). */
  min?: StoreRole;
}

// TNK-132: tools grouped into categories so the workbench reads as a
// focused set of utilities, not an undifferentiated bag of 18 tiles.
// Three calculators (ROI / Break-Even / Profit Analytics) collapsed
// into a single Profitability entry — the page mounts a shared tab
// strip so the three views feel like one tool. eBay Integration,
// Listing Templates, and Bundle Settings moved to /settings/* via
// thin redirects.
interface ToolGroup {
  title: string;
  description: string;
  tools: ToolEntry[];
}

const TOOL_GROUPS: ToolGroup[] = [
  {
    title: "Imports",
    description: "Bring cards into your catalog — files, eBay, or scans.",
    tools: [
      {
        to: "/tools/imports",
        title: "Import Cards",
        description:
          "Staged CSV import — pick a file, review every row, confirm to commit.",
        Icon: Upload,
        // TNK-86 verify catch: staged imports run against /api/imports/*
        // which is Manager+ end-to-end (drafts, overrides, rollback). Staff
        // add inventory via /inventory/create + /inventory/bulk instead.
        min: "manager",
      },
      {
        to: "/tools/listing-import",
        title: "Import eBay Listings",
        description:
          "Pull existing eBay active listings and attach them to products in this catalog.",
        Icon: ListPlus,
        min: "manager",
      },
      {
        to: "/tools/scan",
        title: "Scan Intake",
        description:
          "Upload card photos (front, or front + back from a duplex scanner) — each is identified automatically, reviewed, then committed into a bin. Resumes if interrupted.",
        Icon: Images,
        min: "manager",
      },
      {
        to: "/tools/intake",
        title: "Chaos Intake",
        description:
          "Pick a bin, then scan or type set + number to drop cards in fast. The database remembers where each one went.",
        Icon: ScanLine,
      },
    ],
  },
  {
    title: "Storage",
    description: "Bins, sleeves, mailers — the physical side of the catalog.",
    tools: [
      {
        to: "/tools/storage-bins",
        title: "Storage Bins",
        description:
          "Manage named storage bins (Box A, Binder 1, etc.) and see which cards live in each.",
        Icon: Inbox,
      },
      {
        to: "/tools/locate",
        title: "Find a Card",
        description:
          "Search by name or scan set + number to see exactly which container(s) hold a card, with quantities.",
        Icon: Search,
      },
      {
        to: "/tools/supplies",
        title: "Packaging Supplies",
        description:
          "Track sleeves, toploaders, mailers, and other consumables with per-unit cost and stock.",
        Icon: Package,
        min: "manager",
      },
    ],
  },
  {
    title: "Calculators",
    description: "Decide whether a sale, a lot, or a stack is worth it.",
    tools: [
      {
        to: "/tools/roi",
        title: "Profitability",
        description:
          "Per-sale ROI, break-even projection, and aggregate analytics — three views, one tool.",
        Icon: Percent,
      },
      {
        to: "/tools/lot-analyzer",
        title: "Lot Analyzer",
        description:
          "Estimate profit and a max bid before you commit to a lot purchase, using your own sales history.",
        Icon: ShoppingCart,
        min: "manager",
      },
      {
        to: "/tools/weight",
        title: "Bulk Weight Estimator",
        description:
          "Estimate card count and cost basis from a stack or box weight.",
        Icon: Scale,
      },
    ],
  },
  {
    title: "Pricing & Insights",
    description: "Comps, trends, and recommendations from your data.",
    tools: [
      {
        to: "/tools/pricetrack",
        title: "Price Tracker",
        description:
          "Search live TCGplayer prices, log your sold comps, and get a suggested listing price after fees.",
        Icon: Tag,
        min: "manager",
      },
      {
        to: "/tools/recommendations",
        title: "Recommendations",
        description:
          "Buying, listing, and pricing insights derived from your inventory and sales history.",
        Icon: Sparkles,
        min: "manager",
      },
      {
        to: "/tools/reports",
        title: "Sales by Location",
        description:
          "Gross sales rolled up your storage tree — see which case or bin actually moves product.",
        Icon: BarChart3,
        min: "manager",
      },
    ],
  },
  {
    title: "Catalog",
    description: "Reference data — sets and decks you can build against.",
    tools: [
      {
        to: "/tools/setmanager",
        title: "Set Database",
        description:
          "Browse the Pokémon TCG catalog. Search by name, series, or PTCGO code; see which sets you own.",
        Icon: Database,
      },
      {
        to: "/tools/deck-templates",
        title: "Saved Decks",
        description:
          "Manage deck-list templates saved from the deck-import screen.",
        Icon: Bookmark,
        min: "manager",
      },
      {
        to: "/tools/deck-search",
        title: "Deck Search",
        description:
          "Search PokemonCard.io for competitive decks (or paste a URL) and import the card list into inventory.",
        Icon: Search,
        min: "manager",
      },
    ],
  },
  {
    title: "Bundles & Maintenance",
    description: "Bundle generator recipes and bulk catalog repairs.",
    tools: [
      {
        to: "/tools/bundle-templates",
        title: "Bundle Templates",
        description:
          "Recipes the Studio uses to find matching cards in your inventory.",
        Icon: Grid3x3,
        min: "manager",
      },
      {
        to: "/tools/costbasis-backfill",
        title: "Cost Basis Backfill",
        description:
          "Fill zero-cost cards & products with a portfolio or per-lot average so ROI math works.",
        Icon: RefreshCw,
        min: "manager",
      },
    ],
  },
  {
    title: "Shows & Outreach",
    description: "Capture and follow up on the people you meet at events.",
    tools: [
      {
        to: "/tools/leads",
        title: "Leads",
        description:
          "Trade-show signups and prize-draw entries \u2014 filter by event, export the consented ones to mail.",
        Icon: Users,
        // Customer PII + email export \u2014 Manager+, matching storefront settings.
        min: "manager",
      },
    ],
  },
];

function ToolsIndexPage() {
  const role = useRole();
  const groups = TOOL_GROUPS.map((g) => ({
    ...g,
    tools: g.tools.filter((t) => !t.min || roleAtLeast(role, t.min)),
  })).filter((g) => g.tools.length > 0);

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Tools</h1>
        <p className="text-sm text-muted-foreground">
          Utilities for inventory, pricing, and analysis.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.title} className="space-y-2">
              <div className="flex items-baseline gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </h2>
                <span className="text-[11px] text-muted-foreground/70">
                  {group.description}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {group.tools.map(({ to, title, description, Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    className="group flex items-start gap-3 rounded-md border border-border bg-card p-4 hover:border-primary/40 hover:bg-card/80"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="text-sm font-semibold text-foreground">
                        {title}
                      </div>
                      <div className="text-xs leading-snug text-muted-foreground">
                        {description}
                      </div>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
